import React from "react"
import { matchPath, useHistory } from "react-router-dom"
import {
  DioboxEvent,
  EventCounts,
  EventGroup,
  GuestSummary,
  Segment,
  Table,
} from "sharedTypes"
import { Box, Center } from "@chakra-ui/react"
import useGuestSelection from "../useGuestSelection"
import DataTable from "./DataTable/DataTable"
import MultipleSelectActions from "./MultipleSelectActions/MultipleSelectActions"
import NoGuestsMatch from "../NoGuestsMatch"

interface TableViewProps {
  tables: Table[]
  guests: GuestSummary[]
  order: string
  allSelected: boolean
  filtered: boolean
  filtersActive: boolean
  chipsActive: boolean
  toggleSelectAll: () => void
  eventGroups: EventGroup[]
  segments: Segment[]
  counts: EventCounts
  onSetOrder: (order: string) => void
  loading: boolean
  loadingMore: boolean
  event: DioboxEvent
  timezone: string
  onScrollEnd?: () => void
  searchString: string
  onClearSearch: () => void
  onClearFilters: () => void
}

const TableView = ({
  tables,
  guests,
  order,
  allSelected,
  filtered,
  filtersActive,
  chipsActive,
  toggleSelectAll,
  eventGroups,
  segments,
  counts,
  onSetOrder,
  loading,
  loadingMore,
  event,
  timezone,
  onScrollEnd,
  searchString,
  onClearSearch,
  onClearFilters,
}: TableViewProps) => {
  const history = useHistory()
  const guestMatch = matchPath<{ guestId: string }>(history.location.pathname, {
    path: "/events/:eventId/guests/:guestId",
  })
  const guestId = guestMatch?.params?.guestId

  const {
    selectedGuestIds,
    handleSelectionChange,
    handleClick,
    deselectAll,
  } = useGuestSelection({
    guests,
    allSelected,
    event,
    enableNavigation: true,
  })

  const hasSelectedGuests = allSelected || selectedGuestIds.length > 0
  const showMultipleSelectActions = hasSelectedGuests
  const multipleSelectHeight = showMultipleSelectActions ? 64 : 0
  // Show the no-match UI whenever any filter (search OR chip OR segment
  // OR guest list) is on and the fetch returned zero. NoGuestsMatch picks
  // the right message + actions based on which axes are active.
  const showNoMatch = !loading && (!!searchString || filtersActive) && guests.length === 0

  return (
    <Box position="relative" height="100%">
      {showNoMatch ? (
        <Center h="full">
          <NoGuestsMatch
            query={searchString}
            filtersActive={filtersActive}
            chipsActive={chipsActive}
            onClearSearch={onClearSearch}
            onClearFilters={onClearFilters}
            layout="inline"
          />
        </Center>
      ) : (
        <DataTable
          guests={guests}
          order={order}
          allSelected={allSelected}
          multipleSelect
          filtered={filtered}
          disabledGuestIds={[]}
          disableMainGuests={false}
          timezone={timezone}
          toggleSelectAll={toggleSelectAll}
          onSelectionChange={handleSelectionChange}
          onClick={handleClick}
          onSetOrder={onSetOrder}
          loading={loading}
          loadingMore={loadingMore}
          onScrollEnd={onScrollEnd}
          activeGuestId={guestId}
        />
      )}
      {showMultipleSelectActions && (
        <Box
          position="absolute"
          top={0}
          left={0}
          right={0}
          height={`${multipleSelectHeight}px`}
          zIndex={2}
        >
          <MultipleSelectActions
            tables={tables}
            eventGroups={eventGroups}
            segments={segments}
            counts={counts}
            onCancel={deselectAll}
            onSelectAll={toggleSelectAll}
          />
        </Box>
      )}
    </Box>
  )
}

export default TableView
